/**
 * Cliente do control plane.
 *
 * Só o plano de controle — o áudio vai por WebRTC direto ao SFU, que ainda não
 * existe no esqueleto (Fase 1 do roadmap).
 */

import type {
  AudioRoute,
  BandScope,
  ChannelState,
  CWUpdate,
  Envelope,
  MessageType,
  PTTDown,
  RXParams,
  Signal,
  Tune,
  TXDenied,
  TXGranted,
} from './protocol'
import { rfStore } from './rfstore'
import { AudioEngine, logAudio, type RogerTone } from './audio'
import { CW_TONE_HZ, ManualKeyer, MorseStreamer, textToSteps, type MorseStep } from './morse'

export interface ConnectOptions {
  /**
   * Indicativo e classe NÃO aparecem aqui de propósito.
   *
   * O servidor os pega da sessão. Aceitá-los do cliente deixaria qualquer um
   * entrar no ar como qualquer outra pessoa — e o indicativo é a identidade
   * inteira deste hobby.
   */
  lat?: number
  lon?: number
  freqKHz?: number
  mode?: string
}

const RECONNECT_MIN_MS = 500
const RECONNECT_MAX_MS = 15_000

export class ControlPlane {
  private ws: WebSocket | null = null
  private audio: AudioEngine | null = null
  private opts: ConnectOptions | null = null
  private backoff = RECONNECT_MIN_MS
  private closedByUser = false
  private pingTimer: number | null = null
  /**
   * Comandos emitidos antes de o socket abrir.
   *
   * Sem isto, sintonizar nos primeiros instantes some sem aviso: o usuário mexe
   * no VFO, nada acontece, e não há como saber por quê. Guardar e enviar ao
   * conectar é o comportamento que a pessoa espera.
   */
  private outbox: Envelope[] = []

  /** Bipes desta estação. Nulos até o perfil de estação chegar. */
  private startTone: RogerTone | null = null
  private rogerTone: RogerTone | null = null

  /**
   * Conta cada aperto e cada solta do PTT.
   *
   * Existe porque o bipe é assíncrono: entre começar o tom e ele acabar, a
   * pessoa pode ter apertado o PTT de novo. Sem este contador, a continuação
   * atrasada de um aperto antigo derrubaria a transmissão nova — o operador
   * ficaria mudo sem nada na tela explicando.
   */
  private pttEpoch = 0
  /**
   * Em qual época estava o pttEpoch quando o ÚLTIMO ptt_down foi mandado.
   *
   * tx_granted/tx_denied são assíncronos — chegam bem depois de mandados. Se
   * a pessoa soltar rápido o suficiente, pttUp() já roda e AVANÇA pttEpoch
   * antes da resposta do servidor chegar. Sem checar isto, um tx_granted
   * atrasado reabriria `transmitting=true` sozinho, sem nenhum evento
   * futuro pra fechar de novo — é a segunda metade do bug do toque duplo.
   */
  private lastPTTDownEpoch = 0

  /**
   * Estado do modo avançado de CW: texto ao vivo, letra por letra.
   *
   * `cwLiveQueue` acumula caracteres digitados que ainda não foram tocados;
   * `drainCWLiveQueue` esvazia num laço, mandando um `cw_update` e um
   * `playTone` por lote (não por tecla — evita reiniciar o oscilador a cada
   * caractere se a pessoa digitar rápido). `cwLiveStreamer` mantém o MESMO
   * estado de gap entre lotes, então o resultado é idêntico a gerar tudo de
   * uma vez com `textToSteps`.
   */
  private cwLiveActive = false
  private cwLiveDraining = false
  private cwLiveQueue = ''
  private cwLiveSent = ''
  private cwLiveStreamer: MorseStreamer | null = null

  /**
   * Chave manual de CW: nenhuma fila, nenhum streamer — o ritmo é a mão do
   * operador, não texto digitado. ManualKeyer mede a duração de cada
   * aperto e chama de volta uma letra decodificada por vez.
   */
  private manualKeyer: ManualKeyer | null = null

  connect(opts: ConnectOptions) {
    this.opts = opts
    this.closedByUser = false
    this.open()
  }

  /**
   * Reconecta AGORA, sem esperar o backoff.
   *
   * Para quando a pessoa volta ao app depois de ele ter ficado em segundo
   * plano: o socket pode ter morrido durante a suspensão, e esperar o próximo
   * passo do backoff deixaria o rádio mudo por vários segundos sem explicação.
   * Não faz nada se a conexão está de pé.
   */
  reconectarAgora() {
    if (this.closedByUser || !this.opts) return
    const st = this.ws?.readyState
    if (st === WebSocket.OPEN || st === WebSocket.CONNECTING) return
    this.backoff = RECONNECT_MIN_MS
    this.open()
  }

  private open() {
    if (!this.opts) return

    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const q = new URLSearchParams()
    if (this.opts.lat !== undefined) q.set('lat', String(this.opts.lat))
    if (this.opts.lon !== undefined) q.set('lon', String(this.opts.lon))
    if (this.opts.freqKHz !== undefined) q.set('freq_khz', String(this.opts.freqKHz))
    if (this.opts.mode) q.set('mode', this.opts.mode)

    const ws = new WebSocket(`${proto}//${location.host}/ws?${q}`)
    this.ws = ws

    ws.onopen = () => {
      this.backoff = RECONNECT_MIN_MS
      rfStore.setConnected(true)
      this.pingTimer = window.setInterval(() => this.send('ping'), 20_000)

      const pending = this.outbox
      this.outbox = []
      for (const env of pending) ws.send(JSON.stringify(env))
    }

    ws.onclose = () => {
      rfStore.setConnected(false)
      rfStore.setTransmitting(false)
      if (this.pingTimer !== null) {
        clearInterval(this.pingTimer)
        this.pingTimer = null
      }
      // O WebRTC não sobrevive a uma queda do control plane: o servidor
      // esquece esta estação (stationID é por conexão, não por operador), e
      // a próxima vez que alguém entrar nesta faixa o SFU não tem para quem
      // renegociar — o RTCPeerConnection velho fica conectado por fora e
      // surdo por dentro, pra sempre, sem erro nenhum aparecer na tela. Sem
      // isto, uma reconexão (rede de celular soluçando, por exemplo) deixa
      // o áudio morto até a pessoa recarregar a página na mão.
      if (this.audio) {
        logAudio('[audio] control plane caiu — desmontando sessão de mídia órfã')
        this.audio.stop()
        this.audio = null
      }
      if (!this.closedByUser) this.scheduleReconnect()
    }

    ws.onerror = () => {
      // onclose sempre vem em seguida; a reconexão é tratada lá.
    }

    ws.onmessage = (ev: MessageEvent<string>) => {
      let env: Envelope
      try {
        env = JSON.parse(ev.data) as Envelope
      } catch {
        return
      }
      this.handle(env)
    }
  }

  private scheduleReconnect() {
    const delay = this.backoff
    // Backoff exponencial com teto: rede móvel oscila e não vale a pena
    // martelar o servidor.
    this.backoff = Math.min(this.backoff * 2, RECONNECT_MAX_MS)
    window.setTimeout(() => this.open(), delay)
  }

  private handle(env: Envelope) {
    switch (env.type) {
      case 'channel_state':
        rfStore.setChannel(env.payload as ChannelState)
        break

      case 'rx_params': {
        // Caminho quente: não notifica o React.
        const p = env.payload as RXParams
        rfStore.setRX(p)
        this.audio?.applyRXParams(p)
        break
      }

      case 'scope':
        // Caminho quente: não notifica o React.
        rfStore.setScope(env.payload as BandScope)
        break

      case 'signal':
        void this.audio?.handleSignal(env.payload as Signal)
        break

      case 'audio_on': {
        const r = env.payload as AudioRoute
        console.info(`áudio de ${r.callsign} entrando`)
        break
      }

      case 'audio_off': {
        const r = env.payload as AudioRoute
        this.audio?.dropStation(r.tx_station_id)
        break
      }

      case 'tx_granted': {
        // Chegou depois de a pessoa já ter soltado (pttUp já avançou o
        // pttEpoch): abrir a transmissão agora não teria mais nenhum
        // evento futuro pra fechar de novo. Ignorar é o correto — ver o
        // comentário de lastPTTDownEpoch.
        if (this.lastPTTDownEpoch !== this.pttEpoch) break
        const g = env.payload as TXGranted
        rfStore.setTXGranted(g.max_seconds)
        rfStore.setTransmitting(true)
        rfStore.setDenial(null)
        // A portadora sobe; o microfone espera o bipe de início, se houver.
        // Abrir os dois juntos faria o tom e a primeira palavra saírem
        // embolados — que é o defeito de quem monta isso às pressas.
        this.audio?.setTransmitting(true)
        if (rfStore.snapshot().channel?.mode === 'CW') {
          // CW nunca abre o microfone — só o tom do Morse sai, e quem
          // gerencia esse tom é sendCW, não o fluxo de bipe de início.
          this.audio?.setMic(false)
        } else {
          void this.openMicAfterTone()
        }
        break
      }

      case 'tx_denied': {
        // Mesmo raciocínio de tx_granted: uma negativa atrasada não pode
        // pisar num pedido mais novo que já mudou o estado por conta própria.
        if (this.lastPTTDownEpoch !== this.pttEpoch) break
        const d = env.payload as TXDenied
        rfStore.setTransmitting(false)
        rfStore.setDenial(d.reason)
        this.audio?.setTransmitting(false)
        break
      }

      case 'pong':
        break

      case 'error':
        console.warn('control plane:', env.payload)
        break
    }
  }

  private send(type: MessageType, payload?: unknown) {
    const env: Envelope = payload === undefined ? { type } : { type, payload }

    if (this.ws?.readyState !== WebSocket.OPEN) {
      // Ping e sinalização WebRTC não fazem sentido enfileirados: o ping é
      // periódico e o SDP fica obsoleto. O resto é intenção do operador.
      if (type === 'ping' || type === 'signal') return
      // Teto pequeno: guardar dezenas de comandos antigos seria pior que perder.
      if (this.outbox.length < 8) this.outbox.push(env)
      return
    }
    this.ws.send(JSON.stringify(env))
  }

  tune(t: Tune) {
    this.send('tune', t)
  }

  pttDown(powerW: number) {
    this.pttEpoch++
    this.lastPTTDownEpoch = this.pttEpoch
    const p: PTTDown = { power_w: powerW }
    this.send('ptt_down', p)
  }

  /**
   * Solta o PTT — mas não imediatamente, se houver roger.
   *
   * A ordem importa e é a de um rádio de verdade: o microfone fecha na hora
   * (você para de ser ouvido), o bipe sai COM A PORTADORA AINDA NO AR, e só
   * então o transmissor cai. Mandar `ptt_up` antes do tom faria o servidor
   * cortar o roteamento de áudio, e o bipe que o navegador acabou de gerar não
   * chegaria a ninguém — bipe que só o próprio operador ouve não sinaliza nada.
   */
  pttUp() {
    const solto = ++this.pttEpoch
    rfStore.setTransmitting(false)
    const audio = this.audio
    const roger = this.rogerTone

    if (!audio || !roger?.steps?.length) {
      audio?.setTransmitting(false)
      this.send('ptt_up')
      return
    }

    audio.setMic(false)
    rfStore.setToning(true)
    void audio.playTone(roger, true).finally(() => {
      rfStore.setToning(false)
      // Se o PTT foi apertado de novo durante o bipe, quem manda é a
      // transmissão nova: derrubá-la aqui emudeceria o operador sem motivo
      // visível.
      if (solto !== this.pttEpoch) return
      audio.setTransmitting(false)
      this.send('ptt_up')
    })
  }

  /**
   * Manda uma mensagem em CW: liga o PTT com o texto, e solta sozinho
   * quando o Morse acaba de tocar — não é "segure para falar", é "digita e
   * manda", que é o que torna CW acessível a quem ainda não sabe o código.
   *
   * O tom sai de verdade pela trilha (mesmo `playTone` dos bipes de roger,
   * com `transmit=true`), então quem já sabe ler Morse por ouvido pode
   * simplesmente ouvir. Quem não sabe lê a cópia decodificada que o
   * servidor manda em RXParams.cw_text — degradada pelo MESMO SNR que o
   * tom está sofrendo, não é informação de graça.
   */
  sendCW(text: string, wpm: number, powerW: number): void {
    const steps = textToSteps(text, wpm)
    if (steps.length === 0 || !this.audio) return

    this.pttEpoch++
    const meuEpoch = this.pttEpoch
    const p: PTTDown = { power_w: powerW, cw_text: text }
    this.send('ptt_down', p)

    const audio = this.audio
    rfStore.setToning(true)
    void audio.playTone({ id: 'cw', name: 'CW', note: '', steps }, true).finally(() => {
      rfStore.setToning(false)
      // Se outra ação (soltar, tunar, mandar de novo) já mudou a época,
      // quem manda é ela — encerrar aqui de novo emudeceria sem motivo.
      if (meuEpoch !== this.pttEpoch) return
      rfStore.setTransmitting(false)
      audio.setTransmitting(false)
      this.send('ptt_up')
    })
  }

  /**
   * Cancela um envio de CW em curso — o operador mudou de ideia no meio.
   *
   * Serve tanto pro "Parar" do modo simples quanto pro "Finalizar" do modo
   * avançado (a diferença entre "terminei a frase" e "desisti no meio" não
   * importa pro protocolo: os dois soltam o PTT do mesmo jeito), por isso
   * também zera o estado ao vivo — inofensivo quando não havia nenhum.
   */
  cancelCW(): void {
    this.cwLiveActive = false
    this.cwLiveDraining = false
    this.cwLiveQueue = ''
    this.cwLiveStreamer = null
    // Força a decodificação do que já foi apertado na chave manual, senão
    // "Finalizar" no meio de uma letra a perderia silenciosamente; e solta
    // o tom se o aperto físico ainda estava em curso.
    this.manualKeyer?.flush()
    this.manualKeyer = null
    this.audio?.stopManualKey()
    rfStore.setCWSent('')
    this.pttEpoch++
    this.audio?.cancelTone()
    rfStore.setToning(false)
    rfStore.setTransmitting(false)
    this.audio?.setTransmitting(false)
    this.send('ptt_up')
  }

  /**
   * Abre uma transmissão CW no modo avançado: SEM texto pronto — o texto
   * cresce caractere a caractere pelas chamadas de `queueCWChars` que vêm
   * depois, cada uma já tocando (e avisando o control plane) assim que
   * chega, em vez de esperar um "Enviar" com a frase inteira. É o que faz
   * a mensagem se formar na tela de quem ESCUTA no mesmo ritmo em que é
   * digitada, não só na de quem manda.
   */
  startCWLive(wpm: number, powerW: number): void {
    if (this.cwLiveActive || !this.audio) return
    this.cwLiveActive = true
    this.cwLiveDraining = false
    this.cwLiveQueue = ''
    this.cwLiveSent = ''
    this.cwLiveStreamer = new MorseStreamer(wpm)
    rfStore.setCWSent('')
    this.pttEpoch++
    const p: PTTDown = { power_w: powerW, cw_text: '' }
    this.send('ptt_down', p)
  }

  /**
   * Enfileira caracteres recém-digitados pra transmissão ao vivo. Sem
   * efeito fora de uma sessão aberta por `startCWLive`.
   */
  queueCWChars(chars: string): void {
    if (!this.cwLiveActive || !chars) return
    this.cwLiveQueue += chars
    if (!this.cwLiveDraining) void this.drainCWLiveQueue()
  }

  /**
   * Esvazia a fila de caracteres ao vivo: pega tudo que já chegou, gera os
   * passos com o streamer (mantendo o estado de gap entre lotes), manda UM
   * `cw_update` com o texto acumulado até ali, e toca o lote inteiro numa
   * chamada só de `playTone`. Repete enquanto a fila continuar recebendo
   * caracteres novos durante a espera.
   */
  private async drainCWLiveQueue(): Promise<void> {
    const audio = this.audio
    const streamer = this.cwLiveStreamer
    if (!audio || !streamer) return
    this.cwLiveDraining = true
    const meuEpoch = this.pttEpoch

    while (this.cwLiveQueue.length > 0) {
      const batch = this.cwLiveQueue
      this.cwLiveQueue = ''
      const steps: MorseStep[] = []
      for (const c of batch) steps.push(...streamer.next(c))
      this.appendCWSent(batch)

      if (steps.length > 0) {
        rfStore.setToning(true)
        await audio.playTone({ id: 'cw', name: 'CW', note: '', steps }, true)
        rfStore.setToning(false)
      }
      // Foi cancelado/soltou/mandou outra coisa enquanto este lote tocava:
      // quem manda agora é essa ação nova, não este dreno desatualizado.
      if (meuEpoch !== this.pttEpoch) {
        this.cwLiveDraining = false
        return
      }
    }
    this.cwLiveDraining = false
  }

  /**
   * Cresce o texto já efetivamente transmitido (`cwLiveSent`), avisa o
   * `rfStore` (pro operador ver o que já saiu) e manda o `cw_update`
   * correspondente — o mesmo passo final tanto pro modo Avançado
   * (`drainCWLiveQueue`, um lote por vez) quanto pra chave manual
   * (`reportKeyedChar`, uma letra por vez).
   */
  private appendCWSent(chunk: string): void {
    this.cwLiveSent += chunk
    rfStore.setCWSent(this.cwLiveSent)
    const upd: CWUpdate = { cw_text: this.cwLiveSent }
    this.send('cw_update', upd)
  }

  /**
   * Abre uma transmissão CW por chave manual: PTT down sem texto nenhum —
   * o texto vai sendo DECODIFICADO do próprio ritmo de aperto/soltura
   * (`ManualKeyer`), não digitado. É o modo mais autêntico: sem tradução
   * automática de texto pra Morse, é a pessoa quem produz o ponto/traço
   * com a duração da própria mão, exatamente como uma chave telegráfica de
   * verdade — e é o que faz o CW parar de parecer um chat de texto.
   */
  startManualKey(wpm: number, powerW: number): void {
    if (this.cwLiveActive || !this.audio) return
    this.cwLiveActive = true
    this.cwLiveSent = ''
    rfStore.setCWSent('')
    this.manualKeyer = new ManualKeyer(wpm, (ch) => this.appendCWSent(ch))
    this.pttEpoch++
    const p: PTTDown = { power_w: powerW, cw_text: '' }
    this.send('ptt_down', p)
  }

  /** Aperta a chave manual: liga o tom AGORA, sem duração pré-definida —
   * `keyUp` é quem decide, pela duração real do aperto, se virou ponto ou
   * traço. Sem efeito fora de uma sessão aberta por `startManualKey`. */
  keyDown(): void {
    if (!this.cwLiveActive || !this.audio || !this.manualKeyer) return
    this.audio.startManualKey(CW_TONE_HZ)
    this.manualKeyer.keyDown(performance.now())
  }

  /** Solta a chave manual: desliga o tom com uma rampa curta e registra a
   * duração do aperto que acabou de terminar. */
  keyUp(): void {
    if (!this.cwLiveActive || !this.audio || !this.manualKeyer) return
    this.audio.stopManualKey()
    this.manualKeyer.keyUp(performance.now())
  }

  /**
   * Escolhe os bipes desta estação.
   *
   * Vêm do perfil de estação, validados pelo servidor: o cliente sintetiza o
   * som, mas não inventa quais tons existem.
   */
  setTones(inicio: RogerTone | null, roger: RogerTone | null) {
    this.startTone = inicio
    this.rogerTone = roger
  }

  /** Toca um bipe só para o operador, sem ir ao ar. É a prévia da configuração. */
  previewTone(tone: RogerTone | null): Promise<void> {
    return this.audio?.playTone(tone, false) ?? Promise.resolve()
  }

  /** Muda a largura do filtro de FI que o cliente aplica ao que já se ouve. */
  setFilterHz(hz: number): void {
    this.audio?.setFilterHz(hz)
  }

  /** Muda o limiar do squelch (SNR em dB), ou desliga com null. */
  setSquelch(db: number | null): void {
    this.audio?.setSquelch(db)
  }

  /** Liga/desliga o chiado de banda — "áudio limpo", só a voz. */
  setNoiseEnabled(on: boolean): void {
    this.audio?.setNoiseEnabled(on)
  }

  /** Tom de "atracou na repetidora" — local, nunca transmitido. */
  playLockTone(digital: boolean): void {
    this.audio?.playLockTone(digital)
  }

  /**
   * Abre o microfone depois do bipe de início.
   *
   * Sem tom de início, abre na hora — que é o comportamento de sempre.
   */
  private async openMicAfterTone(): Promise<void> {
    const audio = this.audio
    if (!audio) return
    const tom = this.startTone
    if (!tom?.steps?.length) {
      audio.setMic(true)
      return
    }
    const epoca = this.pttEpoch
    audio.setMic(false)
    rfStore.setToning(true)
    await audio.playTone(tom, true)
    rfStore.setToning(false)
    // Soltou o PTT antes do tom acabar: abrir o microfone agora seria transmitir
    // depois de a pessoa ter mandado parar.
    if (epoca !== this.pttEpoch) return
    audio.setMic(true)
  }

  /**
   * Destrava o AudioContext AGORA, dentro do gesto síncrono do usuário —
   * o clique de entrar/cadastrar. O resto de ligar o áudio (microfone,
   * WebRTC) roda depois, de dentro de um efeito, sem precisar de gesto
   * nenhum — mas o AudioContext em si só existe se for criado aqui, sem
   * `await` nenhum antes, senão no iOS ele nasce travado (AudioEngine.unlock).
   */
  unlockAudio(): void {
    if (this.audio) return
    const engine = new AudioEngine({
      sendSignal: (sig) => this.send('signal', sig),
      onStateChange: (st) => rfStore.setMediaState(st),
    })
    engine.unlock()
    this.audio = engine
  }

  /**
   * Liga o áudio de verdade: pede microfone e abre a conexão WebRTC.
   * Reaproveita o AudioContext de unlockAudio(), se já tiver sido chamado —
   * é o caminho normal, disparado sozinho quando a conexão abre. Chamado
   * direto (sem unlockAudio antes) só funciona se ESTA chamada também
   * estiver dentro de um gesto do usuário.
   */
  async enableAudio(): Promise<void> {
    const engine =
      this.audio ??
      new AudioEngine({
        sendSignal: (sig) => this.send('signal', sig),
        onStateChange: (st) => rfStore.setMediaState(st),
      })
    this.audio = engine
    try {
      await engine.start()
    } catch (err) {
      this.audio = null
      throw err
    }
  }

  get audioEnabled(): boolean {
    return this.audio !== null
  }

  disconnect() {
    this.closedByUser = true
    this.audio?.stop()
    this.audio = null
    this.ws?.close()
    this.ws = null
  }
}

export const controlPlane = new ControlPlane()

// --- REST ---------------------------------------------------------------

/**
 * Segmento do plano de bandas.
 *
 * `classes` vazio significa SEM restrição de classe; `modes` vazio significa
 * farol ou satélite, onde ninguém transmite. As duas convenções vêm do formato
 * de dados do servidor e são o que a fita de dial usa para pintar privilégio.
 */
export interface BandSegment {
  low_khz: number
  high_khz: number
  modes?: string[]
  classes?: string[]
  /** Só na Faixa do Cidadão: o número do canal. */
  channel?: string
  note?: string
}

export interface Band {
  id: string
  name: string
  low_khz: number
  high_khz: number
  default_sideband: string
  propagation_profile: string
  segments?: BandSegment[]
  /** Serviço quando não é radioamadorismo. "SRC" é a Faixa do Cidadão. */
  service?: string
  /** Faixa sem exame nem classes: qualquer operador transmite. */
  open_to_all?: boolean
  max_power_w?: number
  counts_awards?: boolean
}

export interface BandsResponse {
  version: string
  source: string
  effective_from: string
  incomplete?: boolean
  warning?: string
  bands: Band[]
  /**
   * Onde colocar quem acabou de chegar — vem do SERVIDOR, da tabela da PX.
   *
   * Fixar a frequência aqui faria o app e a canalização discordarem no dia em
   * que a norma mudasse, e o app não avisaria.
   */
  start?: {
    freq_khz: number
    mode: string
    band_id: string
    channel: string
    why: string
  }
}

/**
 * Se um operador desta classe pode transmitir neste segmento.
 *
 * `aberta` marca faixa sem exame — a Faixa do Cidadão. Ali a classe não entra
 * na conta, porque não existe: é serviço aberto, e é o único lugar onde quem
 * acabou de chegar consegue falar.
 */
export function segmentAllows(seg: BandSegment, classe: string, aberta = false): boolean {
  // Sem modos é farol, satélite, telecomando ou canal de emergência: ninguém
  // conversa ali, em nenhum serviço.
  if (!seg.modes || seg.modes.length === 0) return false
  if (aberta) return true
  // Sem classe declarada, o segmento não restringe por classe.
  if (!seg.classes || seg.classes.length === 0) return true
  return classe !== '' && seg.classes.includes(classe)
}

export async function fetchBands(): Promise<BandsResponse> {
  const r = await fetch('/api/bands')
  if (!r.ok) throw new Error(`GET /api/bands: ${r.status}`)
  return (await r.json()) as BandsResponse
}
